import React, { useEffect, useState } from "react";
import { BackButton, EditButton } from "../../../../Components/Utils/Buttons";
import { Link, useLocation, useParams } from "react-router-dom";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { NormalHeading } from "../../../../Components/Utils/Heading";
import NuDateDisplay from "../../../../Components/Utils/NuDate";
import axiosInstance from "../../../../Services/axiosCommon";

const ViewContracts = () => {
  const { contractsViewId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [data, setData] = useState(stateData || null);
  const [loading, setLoading] = useState(!stateData);

  useEffect(() => {
    if (!stateData && contractsViewId) {
      setLoading(true);
      axiosInstance
        .get(`/contracts/view-contract/${contractsViewId}`)
        .then((res) => {
          if (res?.data?.data) {
            setData(res.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching contract details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [stateData, contractsViewId]);

  const targetId = contractsViewId || data?.id || data?.contractId;

  return (
    <div className="w-full h-full p-2 lg:px-4">
      <div className="flex justify-between items-center mb-4">
        <Breadcrumb
          items={[
            { name: "Procurement" },
            {
              name: "Contracts",
              link: "/procurement/contracts",
            },
            {
              name: "View Contracts",
            },
          ]}
        />
        <div className="flex gap-2">
          <Link
            to="/procurement/contracts"
            state={{
              currentPage: pageIndex,
              searchQuery: searchQuery,
            }}
          >
            <BackButton link={true} />
          </Link>
          <Link
            to={`/procurement/contracts/edit/${targetId}`}
            state={{
              data: data,
              from: `/procurement/contracts/view/${targetId}`,
              pageIndex: pageIndex,
              searchQuery: searchQuery,
            }}
          >
            <EditButton height="8" />
          </Link>
        </div>
      </div>

      <div className="w-full mx-auto h-[calc(100vh-140px)] bg-white rounded-md overflow-y-auto p-4">
        <NormalHeading title={"Contract Details"} />

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <span className="text-sm font-medium text-gray-500">
              Loading contract details...
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            {/* Primary Details */}
            <div className="rounded-xl border border-gray-200/80 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200/80">
                <h3 className="text-xs 2xl:text-sm font-semibold text-[#111537]">
                  General Information
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Contract ID</p>
                  <p className="w-1/2 text-[#606279]">{data?.id || data?.contractId || "-"}</p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Contract Title</p>
                  <p className="w-1/2 text-[#606279]">{data?.contractTitle || data?.title || "-"}</p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Customer Name</p>
                  <p className="w-1/2 text-[#606279]">{data?.customerName || "-"}</p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Scope Of Work</p>
                  <p className="w-1/2 text-[#606279] whitespace-pre-wrap">{data?.scopeOfWork || "-"}</p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Status</p>
                  <div className="w-1/2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        data?.status === "Approved" || data?.status === "Completed"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                          : data?.status === "Pending"
                          ? "bg-amber-50 text-amber-700 border-amber-100"
                          : "bg-gray-50 text-gray-700 border-gray-200"
                      }`}
                    >
                      {data?.status || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline & Duration */}
            <div className="rounded-xl border border-gray-200/80 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200/80">
                <h3 className="text-xs 2xl:text-sm font-semibold text-[#111537]">
                  Timeline & Duration
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Signed On</p>
                  <p className="w-1/2 text-[#606279]">
                    {data?.signedOn
                      ? NuDateDisplay({ value: data.signedOn, format: "DD-MMM-YYYY" })
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Start Date</p>
                  <p className="w-1/2 text-[#606279]">
                    {data?.startDate
                      ? NuDateDisplay({ value: data.startDate, format: "DD-MMM-YYYY" })
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">End Date</p>
                  <p className="w-1/2 text-[#606279]">
                    {data?.endDate
                      ? NuDateDisplay({ value: data.endDate, format: "DD-MMM-YYYY" })
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Project Duration</p>
                  <p className="w-1/2 text-[#606279]">
                    {data?.projectDuration ? `${data.projectDuration} days` : "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="rounded-xl border border-gray-200/80 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200/80">
                <h3 className="text-xs 2xl:text-sm font-semibold text-[#111537]">
                  Financials
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Contract Amount</p>
                  <p className="w-1/2 text-[#606279] font-medium">
                    {data?.contractAmount !== undefined && data?.contractAmount !== null
                      ? Number(data.contractAmount).toLocaleString()
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Additional Amount</p>
                  <p className="w-1/2 text-[#606279]">
                    {data?.additionalAmount !== undefined && data?.additionalAmount !== null
                      ? Number(data.additionalAmount).toLocaleString()
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Total Amount</p>
                  <p className="w-1/2 font-semibold text-[#048178]">
                    {data?.totalAmount !== undefined && data?.totalAmount !== null
                      ? Number(data.totalAmount).toLocaleString()
                      : "-"}
                  </p>
                </div>
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Receivables</p>
                  <p className="w-1/2 text-[#606279]">{data?.receivables || "-"}</p>
                </div>
              </div>
            </div>

            {/* Additional Information & Notes */}
            <div className="rounded-xl border border-gray-200/80 overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200/80">
                <h3 className="text-xs 2xl:text-sm font-semibold text-[#111537]">
                  Notes & Attachments
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                <div className="flex px-4 py-2.5 text-xs 2xl:text-sm">
                  <p className="w-1/2 font-medium text-[#111537]">Comments</p>
                  <p className="w-1/2 text-[#606279] whitespace-pre-wrap">{data?.comments || "-"}</p>
                </div>
                {data?.attachments && (
                  <div className="flex px-4 py-2.5 text-xs 2xl:text-sm items-center">
                    <p className="w-1/2 font-medium text-[#111537]">Attachment</p>
                    <a
                      href={data.attachments}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#048178] hover:underline font-medium"
                    >
                      View Attachment
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewContracts;